/**
 * @module
 *
 * @import {Valid} from '@acng/frontend-rubicon';
 */

import {ARRAY, BOOLEAN, INTEGER, NULLABLE, ENUM, ENTRIES, AND} from '@acng/frontend-rubicon';
import {guard, OBJECT, RECORD, STRING, UNKNOWN} from '@acng/frontend-rubicon';
import {MapStore} from '@acng/frontend-bounty/collection.js';
import {entries} from '@acng/frontend-bounty/object.js';
import {map} from '@acng/frontend-bounty/std/array.js';

import {cdn} from '../../core/service/cdn.js';
import {IMAGE_DATA} from '../../model';

const FIELD = /**
 * @type {const}
 */ (['description', 'zip', 'city', 'birthday', 'height', 'weight', 'penislength', 'penisperimeter']);

const FIELD_SELECT = /**
 * @type {const}
 */ ([
  'gender',
  'ethnic',
  'eyecolor',
  'haircolor',
  'hairlength',
  'body',
  'bodyhair',
  'pubichair',
  'brasize',
  'glasses',
  'tattoos',
  'piercings',
  'marital',
  'smoking',
]);

/**
 * @typedef {(typeof FIELD_SELECT)[number]} SelectName
 */

const FILLABLE = /**
 * @type {const}
 */ ([...FIELD, ...FIELD_SELECT, 'country']);

const VALID_FILLABLE = /* @__PURE__ */ ENUM(FILLABLE);

const translations = MapStore(
  /**
   * @param {'de' | 'en'} lang
   */
  async (lang) => {
    const data = await cdn(`/language/${lang}/models`);
    ASSERT: {
      const VALID = OBJECT({
        models: OBJECT({
          UserProfile: UNKNOWN,
          AmateurProfile: UNKNOWN,
        }),
      });
      guard(data, VALID);
    }

    return data;
  },
);

/**
 * @template {SelectName} T
 *
 * @param {T} name
 *
 * @returns {`${T}_attributes`}
 */
const mapName = (name) => `${name}_attributes`;

/**
 * @typedef {{value: string; text: string}} Option
 */

const getOptions = MapStore(
  /**
   * @param {'de' | 'en'} lang
   */
  (lang) =>
    MapStore(
      /**
       * They seem to be already sorted by some kind of relevance.
       *
       * @param {SelectName} name
       *
       * @returns {Promise<Option[]>}
       */
      async (name) => {
        const data = await translations(lang);
        const all = data.models.UserProfile;
        ASSERT: guard(all, RECORD(ENUM(FIELD_SELECT.map(mapName)), RECORD(STRING, STRING)));

        const options = all[mapName(name)];
        return map(entries(options), ([value, text]) => ({value, text}));
      },
    ),
);

const PREFERENCE = 'preference';
const LOOKINGFOR = 'lookingfor';
const EXPERIENCE = 'experience';

/**
 * @type {[PREFERENCE, LOOKINGFOR, EXPERIENCE]}
 */
const FIELD_PREFERENCE = [PREFERENCE, LOOKINGFOR, EXPERIENCE];

/**
 * @typedef {(typeof FIELD_PREFERENCE)[number]} PreferenceName
 */

const getPreferences = MapStore(
  /**
   * @param {'de' | 'en'} lang
   */
  (lang) =>
    MapStore(
      /**
       * They seem to be already sorted by some kind of relevance.
       *
       * @param {PreferenceName} name
       *
       * @returns {Promise<Option[]>}
       */
      async (name) => {
        /**
         * @template {PreferenceName} T
         *
         * @param {T} name
         *
         * @returns {`${T}_list`}
         */
        const mapKey = (name) => `${name}_list`;

        const data = await translations(lang);
        const all = data.models.AmateurProfile;
        ASSERT: guard(all, ENTRIES(ENUM(FIELD_PREFERENCE.map(mapKey)), RECORD(STRING, STRING)));
        const options = all[mapKey(name)];
        return map(entries(options), ([value, text]) => ({value: value.substring(1), text}));
      },
    ),
);

const USER_PROFILE_DATA = /* @__PURE__ */ (() =>
  OBJECT({
    id: INTEGER,
    user_pool_id: INTEGER,
    nickname: STRING,
    active: BOOLEAN,

    birthday: NULLABLE(STRING),
    city: NULLABLE(STRING),
    country: NULLABLE(STRING),
    state: NULLABLE(STRING),
    zip: STRING,

    created_at: STRING, // iso (timezone missing?)
    first_login: STRING, // iso (timezone missing?)
    description: NULLABLE(STRING),

    experience: ARRAY(INTEGER),
    lookingfor: ARRAY(INTEGER),
    preference: ARRAY(INTEGER),

    gender: STRING,
    body: NULLABLE(INTEGER),
    bodyhair: NULLABLE(INTEGER),
    brasize: NULLABLE(INTEGER),
    ethnic: NULLABLE(INTEGER),
    eyecolor: NULLABLE(INTEGER),
    glasses: NULLABLE(INTEGER),
    haircolor: NULLABLE(INTEGER),
    hairlength: NULLABLE(INTEGER),
    height: NULLABLE(INTEGER),
    weight: NULLABLE(INTEGER),
    marital: NULLABLE(INTEGER),
    penislength: NULLABLE(INTEGER),
    penisperimeter: NULLABLE(INTEGER),
    piercings: NULLABLE(INTEGER),
    pubichair: NULLABLE(INTEGER),
    smoking: NULLABLE(INTEGER),
    tattoos: NULLABLE(INTEGER),

    image_fsk: INTEGER,
    images: NULLABLE(
      AND(IMAGE_DATA,
      OBJECT({
        normal: OBJECT({
          60: STRING,
          120: STRING,
          200: STRING,
          320: STRING,
        }),
        wide: OBJECT({
          160: STRING,
          480: STRING,
          900: STRING,
        }),
      }),
    ),
    ),
  }))();

/**
 * @typedef {Valid<USER_PROFILE_DATA>} UserProfileData
 */

const asWaitingImage = /* @__PURE__ */ NULLABLE(IMAGE_DATA);

/**
 * @typedef {Valid<typeof asWaitingImage>} UserProfileWaitingImage
 */

export {getOptions, getPreferences, VALID_FILLABLE, USER_PROFILE_DATA, asWaitingImage};
